import React, { forwardRef, useState } from 'react';
import { IndianRupee, AlertCircle } from 'lucide-react';

/**
 * AmountInput Component
 * 
 * Specialized input for currency amounts with formatting.
 * Handles decimal precision, thousands separators, and validation.
 * 
 * @example
 * ```tsx
 * <AmountInput
 *   label="Amount"
 *   value={amount}
 *   onChange={setAmount}
 *   currency="USD"
 * />
 * ```
 */

export interface AmountInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type' | 'size'> {
  /** Label text */
  label?: string;
  /** Current value (as number) */
  value?: number | string;
  /** Change handler (receives number) */
  onChange?: (value: number) => void;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Currency symbol */
  currency?: string;
  /** Decimal places (default: 2) */
  decimals?: number;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full width */
  fullWidth?: boolean;
  /** Container className */
  containerClassName?: string;
  /** Allow negative values */
  allowNegative?: boolean;
}

const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    {
      label,
      value,
      onChange,
      error,
      helperText,
      currency = '₹',
      decimals = 2,
      min,
      max,
      size = 'md',
      fullWidth = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      required,
      allowNegative = false,
      ...props
    },
    ref
  ) => {
    const inputId = id || `amount-${React.useId()}`;
    
    // Internal display value (formatted string)
    const [displayValue, setDisplayValue] = useState(() => {
      if (value === undefined || value === null || value === '') return '';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(num) ? '' : num.toFixed(decimals);
    });

    // Format number with commas
    const formatNumber = (num: number): string => {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    };

    // Parse display value to number
    const parseValue = (str: string): number => {
      const cleaned = str.replace(/[^0-9.-]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    };

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;
      
      // Remove currency symbol and commas
      inputValue = inputValue.replace(/[^0-9.-]/g, '');
      
      // Handle negative values
      if (!allowNegative && inputValue.startsWith('-')) {
        inputValue = inputValue.substring(1);
      }
      
      // Update display
      setDisplayValue(inputValue);
      
      // Parse and validate
      const numValue = parseFloat(inputValue);
      
      if (!isNaN(numValue)) {
        // Apply min/max constraints
        let finalValue = numValue;
        if (min !== undefined && finalValue < min) finalValue = min;
        if (max !== undefined && finalValue > max) finalValue = max;
        
        onChange?.(finalValue);
      } else if (inputValue === '' || inputValue === '-') {
        onChange?.(0);
      }
    };

    // Handle blur - format the value
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (displayValue !== '') {
        const num = parseValue(displayValue);
        setDisplayValue(num.toFixed(decimals));
      }
      props.onBlur?.(e);
    };

    // Handle focus - remove formatting for editing
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (displayValue !== '') {
        const num = parseValue(displayValue);
        setDisplayValue(num.toString());
      }
      props.onFocus?.(e);
    };

    // Update display when external value changes
    React.useEffect(() => {
      if (value !== undefined && value !== null) {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(num)) {
          const newDisplayValue = num.toFixed(decimals);
          // Only update if the value actually changed to prevent infinite loops
          setDisplayValue(prev => {
            const prevNum = parseFloat(prev);
            return isNaN(prevNum) || Math.abs(prevNum - num) > 0.001 ? newDisplayValue : prev;
          });
        }
      }
    }, [value, decimals]);

    // Base styles
    const baseStyles = [
      'bg-white border rounded-lg',
      'text-gray-900 placeholder-gray-400',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60',
      'dark:bg-dark-card dark:text-dark-text',
      'dark:placeholder-gray-500 dark:disabled:bg-dark-surface',
    ].join(' ');

    // State styles
    const stateStyles = error
      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
      : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-dark-border';

    // Size styles - MUCH MORE padding to prevent icon overlap
    const sizeStyles = {
      sm: 'pl-12 pr-3 py-1.5 text-sm',   // Increased to pl-12 (48px)
      md: 'pl-14 pr-3 py-2.5 text-base', // Increased to pl-14 (56px)
      lg: 'pl-16 pr-4 py-3 text-lg',     // Increased to pl-16 (64px)
    };

    // Icon size
    const iconSize = {
      sm: 14,
      md: 16,
      lg: 20,
    };

    // Icon wrapper width to reserve space on the left consistently
    const iconWrapperWidth = {
      sm: 'w-10',  // 40px reserved area
      md: 'w-12',  // 48px reserved area
      lg: 'w-14',  // 56px reserved area
    };

    const widthStyles = fullWidth ? 'w-full' : '';
    const containerWidth = fullWidth ? 'w-full' : '';

    const inputStyles = [
      baseStyles,
      stateStyles,
      sizeStyles[size],
      widthStyles,
      className,
    ].join(' ');

    return (
      <div className={`${containerWidth} ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-900 mb-1.5 dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Currency Icon - fixed-width left slot ensures consistent spacing */}
          <div
            className={`absolute inset-y-0 left-0 ${iconWrapperWidth[size]} text-gray-700 dark:text-gray-300 pointer-events-none z-10 flex items-center justify-center`}
          >
            <IndianRupee size={iconSize[size]} strokeWidth={2.5} />
          </div>

          {/* Input */}
          <input
            ref={ref}
            type="text"
            id={inputId}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            required={required}
            className={inputStyles}
            style={{
              paddingLeft: size === 'sm' ? '44px' : size === 'lg' ? '60px' : '52px'
            }}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                ? `${inputId}-helper`
                : undefined
            }
            {...props}
          />

          {/* Error Icon */}
          {error && (
            <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-error-500`}>
              <AlertCircle size={iconSize[size]} />
            </div>
          )}
        </div>

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <p
            id={error ? `${inputId}-error` : `${inputId}-helper`}
            className={`mt-1.5 text-sm ${
              error
                ? 'text-error-600 dark:text-error-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

AmountInput.displayName = 'AmountInput';

export default AmountInput;
